import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../config';
import { 
  ArrowDownTrayIcon,
  ArrowPathIcon,
  TrashIcon,
  ClipboardDocumentIcon
} from '@heroicons/react/24/outline';

function BulkDataExport() {
  const [bulkDataStatus, setBulkDataStatus] = useState(null);
  const [bulkDataFiles, setBulkDataFiles] = useState([]);
  const [isPolling, setIsPolling] = useState(false);
  const [resourceTypes, setResourceTypes] = useState('Patient,Observation');
  const [bulkDataGroupId, setBulkDataGroupId] = useState('e3iabhmS8rsueyz7vaimuiaSmfGvi.QwjVXJANlPOgR83');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleBulkDataExport = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setBulkDataStatus(null);
    setBulkDataFiles([]);

    try {
      console.log('Initiating bulk data export with:', {
        groupId: bulkDataGroupId,
        type: resourceTypes
      });
      
      const response = await axios.post(`${API_URL}/api/bulk-data/export`, {
        groupId: bulkDataGroupId,
        type: resourceTypes
      });
      
      console.log('Bulk data export response:', response.data);
      
      // Set the initial status
      setBulkDataStatus({
        status: 'in-progress',
        statusUrl: response.data.statusUrl,
        ...response.data
      });

      // Start polling immediately
      startPolling(response.data.statusUrl);
    } catch (err) {
      console.error('Bulk data export error:', err.response?.data || err.message);
      setError(err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };
  let pollingInterval = null;
  const startPolling = (statusUrl) => {
    if (isPolling) {
      console.log('Already polling, stopping previous poll');
      stopPolling();
    }

    console.log('Starting polling with status URL:', statusUrl);
    setIsPolling(true);
    
    pollingInterval = setInterval(async () => {
      try {
        console.log('Polling status URL:', statusUrl);
        const response = await axios.get(`${API_URL}/api/bulk-data/status`, {
          params: { statusUrl }
        });
        
        console.log('Polling response:', response.data);
        
        // Update the status with the latest response
        setBulkDataStatus(prevStatus => ({
          ...prevStatus,
          ...response.data
        }));
        console.log(response.data.status);
        if (response.data.status === 'complete') {
          console.log('Bulk data export complete');
          // Get the file URL from the status URL by removing the status endpoint
          const fileUrl = statusUrl.replace('/status', '');
          setBulkDataStatus(prevStatus => ({
            ...prevStatus,
            status: 'complete',
            fileUrl: fileUrl
          }));
          stopPolling();
        } else if (response.data.status === 'error') {
          console.error('Bulk data export failed');
          setError(response.data.error || 'Bulk data export failed');
          stopPolling();
        }
      } catch (err) {
        console.error('Polling error:', err);
        setError(err.response?.data || err.message);
        stopPolling();
      }
    }, 30000); // Poll every 30 seconds
    
  };

  const stopPolling = () => {
      console.log('Stopping polling');
      clearInterval(pollingInterval);
      setIsPolling(false);
  };

  const handleDownloadFile = async (fileUrl) => {
    try {
      const response = await axios.get(`${API_URL}/api/bulk-data/download`, {
        params: { fileUrl }
      });
      
      console.log('Bulk data response:', response.data);
      
      // Handle the bulk data response format
      if (response.data.output && Array.isArray(response.data.output)) {
        // Download each file in the output array
        for (const output of response.data.output) {
          try {
            const fileResponse = await axios.get(`${API_URL}/api/bulk-data/download`, {
              params: { fileUrl: output.url }
            });
            
            // Convert NDJSON to proper JSON array
            const ndjsonData = typeof fileResponse.data === 'string' 
              ? fileResponse.data
              : JSON.stringify(fileResponse.data);
            
            // Split by newlines and parse each line as JSON
            const jsonArray = ndjsonData
              .split('\n')
              .filter(line => line.trim()) // Remove empty lines
              .map(line => JSON.parse(line));
            
            // Create a blob from the converted JSON array
            const jsonString = JSON.stringify(jsonArray, null, 2);
            const blob = new Blob([jsonString], { type: 'application/json' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${output.type.toLowerCase()}_data.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          } catch (fileErr) {
            console.error(`Error downloading file for ${output.type}:`, fileErr);
            setError(`Failed to download ${output.type} data: ${fileErr.message}`);
          }
        }
      } else {
        // Handle single file download (fallback)
        const ndjsonData = typeof response.data === 'string'
          ? response.data
          : JSON.stringify(response.data);
        
        // Convert NDJSON to proper JSON array
        const jsonArray = ndjsonData
          .split('\n')
          .filter(line => line.trim()) // Remove empty lines
          .map(line => JSON.parse(line));
        
        const jsonString = JSON.stringify(jsonArray, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${fileUrl.split('/').pop() || 'bulk-data'}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Download error:', err);
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleDeleteExport = async () => {
    if (!bulkDataStatus?.deleteUrl) return;
    
    try {
      await axios.delete(`${API_URL}/api/bulk-data/delete`, {
        params: { deleteUrl: bulkDataStatus.deleteUrl }
      });
      
      setBulkDataStatus(null);
      setBulkDataFiles([]);
      stopPolling();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const renderBulkDataResults = () => {
    if (!bulkDataStatus) return null;

    return (
      <div className="mt-8">
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Bulk Data Export Status
            </h3>
            {bulkDataStatus.deleteUrl && (
              <button
                onClick={handleDeleteExport}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200"
              >
                <TrashIcon className="h-5 w-5 mr-2" />
                Delete Export
              </button>
            )}
          </div>
          
          <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
            <div className="mb-4">
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-500 mr-2">Status:</span>
                <span className={`text-sm font-medium ${
                  bulkDataStatus.status === 'complete' ? 'text-green-600' :
                  bulkDataStatus.status === 'error' ? 'text-red-600' :
                  'text-yellow-600'
                }`}>
                  {bulkDataStatus.status?.toUpperCase()}
                  {bulkDataStatus.progress && ` (${bulkDataStatus.progress})`}
                </span>
                {isPolling && (
                  <ArrowPathIcon className="h-5 w-5 ml-2 animate-spin text-gray-500" />
                )}
              </div>
              
              <div className="mt-4">
                <span className="text-sm font-medium text-gray-500 mr-2">Download URL:</span>
                <div className="mt-1 flex items-center">
                  {bulkDataStatus.status === 'complete' ? (
                    <button
                      onClick={() => handleDownloadFile(bulkDataStatus.fileUrl)}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200"
                    >
                      <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                      Download Export File
                    </button>
                  ) : (
                    <>
                        Available once request is complete.
                    </>
                  )}
                </div>
              </div>
            </div>

            {bulkDataFiles.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Available Files</h4>
                <div className="space-y-2">
                  {bulkDataFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 truncate">{file.url.split('/').pop()}</p>
                        <a
                          href={file.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary-600 hover:text-primary-800 break-all"
                        >
                          {file.url}
                        </a>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(file.url);
                          }}
                          className="p-1 text-gray-500 hover:text-gray-700 focus:outline-none"
                          title="Copy File URL"
                        >
                          <ClipboardDocumentIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDownloadFile(file.url)}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200"
                        >
                          <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                          Download
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderError = (error) => {
    if (!error) return null;

    // Handle FHIR API errors
    if (error.details?.fhirError) {
      return (
        <div className="mt-4 rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">FHIR API Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p className="font-medium">{error.details.message}</p>
                <p className="mt-1">Status: {error.details.status} {error.details.statusText}</p>
                {error.details.fhirError.issue && (
                  <div className="mt-2">
                    <p className="font-medium">Issues:</p>
                    <ul className="list-disc pl-5 mt-1">
                      {error.details.fhirError.issue.map((issue, index) => (
                        <li key={index}>
                          {issue.details?.text || issue.diagnostics || 'Unknown issue'}
                          {issue.severity && ` (${issue.severity})`}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Handle other errors
    return (
      <div className="mt-4 rounded-md bg-red-50 p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">
              {error.details?.message || error.message || 'An unknown error occurred'}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow">
          <div className="px-4 py-5 sm:p-6">
            <form onSubmit={handleBulkDataExport} className="space-y-6">
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  Group ID
                </label>
                <input
                  type="text"
                  value={bulkDataGroupId}
                  onChange={(e) => setBulkDataGroupId(e.target.value)}
                  placeholder="Enter Group ID"
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Default Group ID is pre-filled but can be changed if needed
                </p>
              </div>

              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  Resource Types
                </label>
                <input
                  type="text"
                  value={resourceTypes}
                  onChange={(e) => setResourceTypes(e.target.value)}
                  placeholder="e.g., Patient,Observation"
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Comma-separated list of resource types to export (e.g., Patient,Observation,Medication)
                </p>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={loading}
                  className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                    loading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    'Start Export'
                  )}
                </button>
              </div>
            </form>

            {error && renderError(error)}

            {renderBulkDataResults()}
          </div>
        </div>
      </div>
    </div>
  );
}

export default BulkDataExport; 